import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { setCoachMarkViewed, setCoachMarksBulk } from '../../../store/actions/coachMark';
import { setUser } from '../../../store/actions/user';
import { CommonService } from '../common/common.service';
import { ConstantService } from '../constant/constant.service';

export type CoachMarkId =
    | 'sortJobsites'
    | 'actionsButton'
    | 'itemDetails'
    | 'jobsiteDetails'
    | 'activePunchEditIcon'
    | 'activePunchMultiSpotlight'
    | 'activePunchActionDetails'
    | 'itemStatusHelp'
    | 'itemAssignedToHelp'
    | 'itemCommentsHelp'
    | 'chatHelp'
    | 'jobsiteClick'
    | 'createPunchItem_mobile'
    | 'createPunchItem_desktop'
    | 'blueDotHelp'
    | 'greenDotHelp'
    | 'orangeDotHelp'
    | 'redDotHelp'
    | 'tradeModalHelp'
    | 'showJobsiteDetailsHelp'
    | 'tradeContactHelp'
    | 'tradeConfirmedHelp'
    | 'whatHappensNext'
    | 'firstPhotoHelp'
    | 'secondItemGuidance'
    | 'hasSeenPunchList_mobile'
    | 'hasSeenPunchList_desktop'
    | 'addPunchHelp_mobile'
    | 'addPunchHelp_desktop'
    | 'hasSeenProjectList_mobile'
    | 'hasSeenProjectList_desktop'
    | 'hasClickedJobsiteDetails'
    | 'hasClickedTradeContacts'
    | 'connectPunchlistDone';

export interface CoachMarkSequence {
    currentId: CoachMarkId;
    nextId: CoachMarkId | null;
}

const ONBOARDING_SEQUENCE: { [key in CoachMarkId]?: CoachMarkId } = {
    activePunchMultiSpotlight: 'activePunchActionDetails',
    activePunchActionDetails: 'activePunchEditIcon',
    activePunchEditIcon: null
};

@Injectable({
    providedIn: 'root'
})
export class CoachMarkService {
    private viewedCoachMarks: { [key: string]: boolean } = {};
    private pendingSyncs: { [key: string]: boolean }[] = [];
    private isSyncing = false;

    /** True once the server has told us what this user has already seen. */
    private hydrated = false;
    /** hydrateFromBackend() has run. Before it, anything in the store is a restored guess. */
    private hydrateStarted = false;

    constructor(
        private store: Store<any>,
        private common: CommonService,
        private constant: ConstantService
    ) {
        this.store.select((state: any) => state.coachMarkData?.viewedCoachMarks).subscribe(
            (viewed) => {
                if (viewed) {
                    this.viewedCoachMarks = viewed;
                }
            }
        );

        // Both login and hydrateFromBackend() land here via setUser. The first emission is
        // whatever the persisted snapshot held — it may merge, but it must not open the gate.
        this.store.select((state: any) => state.user?.user?.onboarding).subscribe(
            (onboarding) => this.applyBackendState(onboarding)
        );
    }

    /**
     * Redux is rebuilt from initialState on every reload/relaunch, so the flags survive only via
     * the persisted snapshot — and that snapshot can be silently missing (quota, cache clear,
     * reinstall, a different device). The server copy is the only durable one, so read it on
     * every boot. Deliberately unconditional: asking local storage whether local storage is
     * trustworthy is how the previous attempt locked itself out of the repair.
     * ponytail: one GET per app start. Upgrade path is to return `onboarding` from
     * userLocalData/readData, which already runs on every boot — then this costs nothing.
     */
    hydrateFromBackend(): void {
        if (this.hydrateStarted) {
            return;
        }
        this.hydrateStarted = true;

        const uid = this.resolveUserId();
        if (!uid) {
            // Logged out. The login that follows dispatches setUser with the server's map,
            // and hydrateStarted is already true, so that emission opens the gate.
            this.markHydrated();
            return;
        }

        this.common.callApi(
            this.constant.USER_DETAIL_V2 + uid,
            '',
            'GET',
            '',
            false,
            false,
            true
        ).subscribe(
            (res: any) => {
                if (res) {
                    this.store.dispatch(setUser(res));
                }
                this.markHydrated();
            },
            () => {
                this.markHydrated();   // offline — fall back to whatever the snapshot held
            }
        );
    }

    private applyBackendState(onboarding: any): void {
        const fromServer = this.hydrateStarted;

        if (onboarding && typeof onboarding === 'object') {
            const merged: { [key: string]: boolean } = {};
            for (const key of Object.keys(onboarding)) {
                if (typeof onboarding[key] === 'boolean') {
                    merged[key] = onboarding[key];
                }
            }
            if (Object.keys(merged).length > 0) {
                this.store.dispatch(setCoachMarksBulk(merged));
            }
        }

        // An empty map is a valid answer for a new user, so hydrate on it too — otherwise
        // queued syncs from before the user id landed would never flush.
        if (fromServer) {
            this.markHydrated();
        }
    }

    private markHydrated(): void {
        if (this.hydrated) {
            return;
        }
        this.hydrated = true;
        this.flushPendingSyncs();
    }

    shouldShowCoachMark(id: CoachMarkId): boolean {
        // Fail closed: a helper skipped once is a non-event, one re-shown after "Got it" is a bug.
        if (!this.hydrated) {
            return false;
        }
        return !this.viewedCoachMarks[id];
    }

    markAsViewed(id: CoachMarkId): void {
        this.store.dispatch(setCoachMarkViewed(id));
        this.syncToBackend({ [id]: true });
    }

    getNextCoachMark(currentId: CoachMarkId): CoachMarkId | null {
        return ONBOARDING_SEQUENCE[currentId] || null;
    }

    shouldShowSortJobsitesHint(jobsiteCount: number): boolean {
        return jobsiteCount >= 5 && this.shouldShowCoachMark('sortJobsites');
    }

    shouldShowJobsiteClickHint(jobsiteCount: number): boolean {
        return jobsiteCount >= 1 && this.shouldShowCoachMark('jobsiteClick');
    }

    shouldShowCreatePunchItemHint(isDesktop: boolean): boolean {
        const key: CoachMarkId = isDesktop ? 'createPunchItem_desktop' : 'createPunchItem_mobile';
        return this.shouldShowCoachMark(key);
    }

    shouldShowBlueDotHint(): boolean {
        return this.shouldShowCoachMark('blueDotHelp');
    }

    shouldShowGreenDotHint(): boolean {
        return this.shouldShowCoachMark('greenDotHelp');
    }

    shouldShowOrangeDotHint(): boolean {
        return this.shouldShowCoachMark('orangeDotHelp');
    }

    shouldShowRedDotHint(): boolean {
        return this.shouldShowCoachMark('redDotHelp');
    }

    isNewUserOnboarding(): boolean {
        const hasSeenActions = this.viewedCoachMarks['actionsButton'];
        const hasSeenItems = this.viewedCoachMarks['itemDetails'];
        const hasSeenDetails = this.viewedCoachMarks['jobsiteDetails'];
        return !hasSeenActions && !hasSeenItems && !hasSeenDetails;
    }

    resetState(): void {
        this.pendingSyncs = [];
        // Logout clears this device only. hydrateStarted stays true, so the next login's setUser
        // counts as server data and re-opens the gate without another fetch.
        this.hydrated = false;
    }

    private syncToBackend(partial: { [key: string]: boolean }): void {
        const uid = this.resolveUserId();

        if (!uid) {
            this.pendingSyncs.push(partial);
            return;
        }

        this.common.callApi(
            this.constant.UPDATE_USER_ONBOARDING,
            { ...partial },
            'PUT',
            '',
            false,
            false,
            true
        ).subscribe(
            () => {},
            () => {
                this.pendingSyncs.push(partial);
            }
        );
    }

    private flushPendingSyncs(): void {
        if (this.isSyncing || this.pendingSyncs.length === 0) return;
        this.isSyncing = true;

        const uid = this.resolveUserId();
        if (!uid) {
            this.isSyncing = false;
            return;
        }

        const merged: { [key: string]: boolean } = {};
        for (const p of this.pendingSyncs) {
            Object.assign(merged, p);
        }
        this.pendingSyncs = [];

        this.common.callApi(
            this.constant.UPDATE_USER_ONBOARDING,
            { ...merged },
            'PUT',
            '',
            false,
            false,
            true
        ).subscribe(
            () => { this.isSyncing = false; },
            () => {
                this.pendingSyncs.push(merged);
                this.isSyncing = false;
            }
        );
    }

    private resolveUserId(): string | null {
        let userId: string | null = null;
        this.store.select((state: any) => state.user?.user).subscribe((user) => {
            userId = user?.id || user?.userID || null;
        }).unsubscribe();
        return userId;
    }
}
